import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Paper, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import { addDoc, collection, doc, getDocs, limit, query, Timestamp, updateDoc } from "firebase/firestore";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { auth, db } from "../Config/firebase";
import { AMBIENTE_PRODUCAO } from "../Config/branding";
import { aplicarTenantAtual, consultaTenantAtual } from "../Utils/tenant";
import { CAMPOS_REQUISITO_LEGAL, COLLECTION_REQUISITOS_LEGAIS, novoRequisitoLegal, REQUISITOS_LEGAIS_INICIAIS } from "./requisitosLegaisData";
import RelatorioRequisitosLegaisPrint from "./RelatorioRequisitosLegaisPrint";

const cabecalho = { bgcolor: "#0b4775", color: "white", border: "1px solid #6b7280", fontWeight: 800, textAlign: "center" };
const celula = { border: "1px solid #557a4c", bgcolor: "white", color: "#2f256f", textAlign: "center", whiteSpace: "normal", overflowWrap: "break-word", lineHeight: 1.25, py: 0.65, cursor: "text", verticalAlign: "middle" };
const larguras = { normaRegulamento: 160, tituloObjeto: 240, versaoDataRevisao: 180, aplicacao: 250, responsavel: 210, evidenciaAtendimento: 230, periodicidadeVerificacao: 190, treinamentoRealizado: 210 };
const estiloPorCampo = {
  normaRegulamento: { color: "#073b66", fontWeight: 800 },
  tituloObjeto: { color: "#7c1d0d", fontWeight: 700 },
  versaoDataRevisao: { color: "#273b8f" },
  aplicacao: { color: "#342b78" },
  responsavel: { color: "#342b78" },
  evidenciaAtendimento: { color: "#342b78" },
  periodicidadeVerificacao: { color: "#342b78" },
  treinamentoRealizado: { color: "#342b78" },
};

function montarCadastro(id, dados) {
  return {
    id,
    revisao: dados.revisao === "00" ? "" : dados.revisao || "",
    atualizadoEmDocumento: dados.atualizadoEmDocumento || "",
    criadoEm: dados.criadoEm || null,
    criadoPor: dados.criadoPor || null,
    criadoPorNome: dados.criadoPorNome || "",
    criadoPorEmail: dados.criadoPorEmail || "",
    atualizadoEm: dados.atualizadoEm || null,
    atualizadoPor: dados.atualizadoPor || null,
    atualizadoPorNome: dados.atualizadoPorNome || "",
    atualizadoPorEmail: dados.atualizadoPorEmail || "",
    historico: Array.isArray(dados.historico) ? dados.historico : [],
    requisitos: (dados.requisitos || []).map((item) => ({ ...novoRequisitoLegal(), ...item })),
  };
}

function copiarCadastro(cadastro) {
  return cadastro ? {
    ...cadastro,
    requisitos: cadastro.requisitos.map((item) => ({ ...item })),
    historico: [...(cadastro.historico || [])],
  } : null;
}

export default function DetalhesRequisitosLegais() {
  const criandoRef = useRef(false);
  const tabelaRef = useRef(null);
  const barraInferiorRef = useRef(null);
  const cadastroSalvoRef = useRef(null);
  const [cadastro, setCadastro] = useState(null);
  const [semCadastro, setSemCadastro] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [alterado, setAlterado] = useState(false);
  const [versaoTabela, setVersaoTabela] = useState(0);
  const [larguraRolagem, setLarguraRolagem] = useState(1900);
  const [indiceExclusao, setIndiceExclusao] = useState(null);
  const [modoRelatorio, setModoRelatorio] = useState(false);
  const [notificacao, setNotificacao] = useState({ aberta: false, mensagem: "", tipo: "success" });

  useEffect(() => { (async () => {
    setCarregando(true);
    try {
      const snapshot = await getDocs(query(consultaTenantAtual(collection(db, COLLECTION_REQUISITOS_LEGAIS)), limit(1)));
      if (snapshot.empty) setSemCadastro(true);
      else {
        const carregado = montarCadastro(snapshot.docs[0].id, snapshot.docs[0].data());
        cadastroSalvoRef.current = copiarCadastro(carregado);
        setCadastro(carregado);
      }
    } catch (erro) {
      console.error("Erro ao carregar requisitos legais:", erro);
      setNotificacao({ aberta: true, mensagem: "Não foi possível carregar os requisitos legais.", tipo: "error" });
    } finally { setCarregando(false); }
  })(); }, []);

  useEffect(() => {
    function atualizarLarguraRolagem() {
      const tabela = tabelaRef.current;
      const barra = barraInferiorRef.current;
      if (!tabela || !barra) return;
      const diferencaConteineres = Math.max(0, barra.clientWidth - tabela.clientWidth);
      setLarguraRolagem(tabela.scrollWidth + diferencaConteineres + 8);
    }
    const quadro = window.requestAnimationFrame(atualizarLarguraRolagem);
    window.addEventListener("resize", atualizarLarguraRolagem);
    return () => {
      window.cancelAnimationFrame(quadro);
      window.removeEventListener("resize", atualizarLarguraRolagem);
    };
  }, [cadastro?.requisitos.length, versaoTabela]);

  async function cadastrarDadosExistentes() {
    if (criandoRef.current) return;
    criandoRef.current = true;
    setSalvando(true);
    try {
      const agora = Timestamp.now();
      const usuario = {
        uid: auth.currentUser?.uid || "",
        nome: auth.currentUser?.displayName || auth.currentUser?.email || "Não informado",
        email: auth.currentUser?.email || "",
      };
      const dados = aplicarTenantAtual({
        codigoDocumento: "RQ 1.8",
        revisao: AMBIENTE_PRODUCAO ? "02" : "",
        atualizadoEmDocumento: AMBIENTE_PRODUCAO ? "25/02/2026" : "",
        requisitos: REQUISITOS_LEGAIS_INICIAIS.map((item) => ({ ...item })),
        criadoEm: agora,
        criadoPor: usuario.uid,
        criadoPorNome: usuario.nome,
        criadoPorEmail: usuario.email,
        atualizadoEm: agora,
        atualizadoPor: usuario.uid,
        atualizadoPorNome: usuario.nome,
        atualizadoPorEmail: usuario.email,
        historico: [{ acao: "CRIACAO", descricao: "Requisitos legais cadastrados com os dados existentes.", usuario, data: agora }],
      });
      const criado = await addDoc(collection(db, COLLECTION_REQUISITOS_LEGAIS), dados);
      const criadoLocal = montarCadastro(criado.id, dados);
      cadastroSalvoRef.current = copiarCadastro(criadoLocal);
      setCadastro(criadoLocal);
      setSemCadastro(false);
      setAlterado(false);
      setNotificacao({ aberta: true, mensagem: "Requisitos legais cadastrados com sucesso.", tipo: "success" });
    } catch (erro) {
      console.error("Erro ao cadastrar requisitos legais:", erro);
      setNotificacao({ aberta: true, mensagem: "Não foi possível cadastrar os requisitos legais.", tipo: "error" });
    } finally {
      criandoRef.current = false;
      setSalvando(false);
    }
  }

  function atualizarCadastro(campo, valor) {
    setCadastro((atual) => {
      if (atual[campo] === valor) return atual;
      setAlterado(true);
      return { ...atual, [campo]: valor };
    });
  }

  function atualizarRequisito(indice, campo, valor) {
    setCadastro((atual) => {
      if (String(atual.requisitos[indice]?.[campo] || "") === valor) return atual;
      setAlterado(true);
      return { ...atual, requisitos: atual.requisitos.map((item, posicao) => posicao === indice ? { ...item, [campo]: valor } : item) };
    });
  }

  function adicionarRequisito() {
    setCadastro((atual) => ({ ...atual, requisitos: [...atual.requisitos, novoRequisitoLegal()] }));
    setAlterado(true);
  }

  function removerRequisito(indice) {
    setCadastro((atual) => ({ ...atual, requisitos: atual.requisitos.filter((_, posicao) => posicao !== indice) }));
    setAlterado(true);
    setIndiceExclusao(null);
  }

  function fecharConfirmacaoExclusao(evento) {
    evento?.stopPropagation?.();
    setIndiceExclusao(null);
  }

  function confirmarExclusao(evento) {
    evento?.stopPropagation?.();
    if (indiceExclusao !== null) removerRequisito(indiceExclusao);
  }

  function cancelarAlteracoes() {
    if (!alterado || !cadastroSalvoRef.current) return;
    setCadastro(copiarCadastro(cadastroSalvoRef.current));
    setAlterado(false);
    setVersaoTabela((atual) => atual + 1);
    setNotificacao({ aberta: true, mensagem: "Alterações canceladas.", tipo: "info" });
  }

  async function salvar() {
    if (cadastro.requisitos.some((item) => !String(item.normaRegulamento || "").trim() || !String(item.tituloObjeto || "").trim())) {
      setNotificacao({ aberta: true, mensagem: "Informe a norma/regulamento e o título/objeto em todas as linhas.", tipo: "warning" });
      return;
    }
    setSalvando(true);
    try {
      const requisitos = cadastro.requisitos.map((item) => Object.fromEntries(CAMPOS_REQUISITO_LEGAL.map(([campo]) => [campo, String(item[campo] || "").trim()])));
      const agora = Timestamp.now();
      const usuario = {
        uid: auth.currentUser?.uid || "",
        nome: auth.currentUser?.displayName || auth.currentUser?.email || "Não informado",
        email: auth.currentUser?.email || "",
      };
      const historicoAnterior = cadastro.historico?.length
        ? cadastro.historico
        : cadastro.criadoEm
          ? [{
              acao: "CRIACAO",
              descricao: "Requisitos legais cadastrados no sistema.",
              usuario: { uid: cadastro.criadoPor || "", nome: cadastro.criadoPorNome || "Não informado", email: cadastro.criadoPorEmail || "" },
              data: cadastro.criadoEm,
            }]
          : [];
      const historico = [...historicoAnterior, { acao: "ALTERACAO", descricao: "Alterações salvas nos requisitos legais.", usuario, data: agora }];
      await updateDoc(doc(db, COLLECTION_REQUISITOS_LEGAIS, cadastro.id), {
        revisao: cadastro.revisao.trim(),
        atualizadoEmDocumento: cadastro.atualizadoEmDocumento.trim(),
        requisitos,
        atualizadoEm: agora,
        atualizadoPor: usuario.uid,
        atualizadoPorNome: usuario.nome,
        atualizadoPorEmail: usuario.email,
        historico,
      });
      const cadastroAtualizado = { ...cadastro, requisitos, atualizadoEm: agora, atualizadoPor: usuario.uid, atualizadoPorNome: usuario.nome, atualizadoPorEmail: usuario.email, historico };
      cadastroSalvoRef.current = copiarCadastro(cadastroAtualizado);
      setCadastro(cadastroAtualizado);
      setAlterado(false);
      setNotificacao({ aberta: true, mensagem: "Requisitos legais salvos com sucesso.", tipo: "success" });
    } catch (erro) {
      console.error("Erro ao salvar requisitos legais:", erro);
      setNotificacao({ aberta: true, mensagem: "Não foi possível salvar os requisitos legais.", tipo: "error" });
    } finally { setSalvando(false); }
  }

  if (modoRelatorio && cadastro) return <RelatorioRequisitosLegaisPrint cadastro={cadastro} onVoltar={() => setModoRelatorio(false)} />;

  return <><Navbar /><Box sx={{ p: { xs: 1, md: 3 }, mt: 6, pb: 5 }}>
    {carregando && <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>}
    {!carregando && semCadastro && <Paper elevation={0} className="cabecalho-lista-cadastro" sx={{ p: 2 }}><Typography variant="h5" fontWeight={700}>RQ 1.8 — Requisitos Legais</Typography><Typography color="text.secondary" sx={{ my: 2 }}>O cadastro é único por empresa. Cadastre os {REQUISITOS_LEGAIS_INICIAIS.length} requisitos existentes para iniciar.</Typography><Button variant="contained" onClick={cadastrarDadosExistentes} disabled={salvando}>{salvando ? "Cadastrando..." : "Cadastrar dados existentes"}</Button></Paper>}
    {cadastro && <><Paper elevation={0} className="cabecalho-lista-cadastro" sx={{ p: 2, mb: 1.5, overflowX: "auto" }}><Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 1520, whiteSpace: "nowrap" }}><Typography variant="h5" fontWeight={700} sx={{ mr: "auto" }}>RQ 1.8 — Requisitos Legais</Typography><TextField size="small" label="Revisão" value={cadastro.revisao} onChange={(event) => atualizarCadastro("revisao", event.target.value)} placeholder="Alterar número da revisão" sx={{ width: 210, flexShrink: 0 }} /><TextField size="small" label="Atualizado em" value={cadastro.atualizadoEmDocumento} onChange={(event) => atualizarCadastro("atualizadoEmDocumento", event.target.value)} placeholder="DD/MM/AAAA" sx={{ width: 180, flexShrink: 0 }} /><HistoricoCadastro colecao={COLLECTION_REQUISITOS_LEGAIS} documentoId={cadastro.id} registro={cadastro} /><Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => setModoRelatorio(true)} sx={{ flexShrink: 0 }}>Exportar PDF</Button><Button variant="outlined" startIcon={<AddIcon />} onClick={adicionarRequisito} sx={{ flexShrink: 0 }}>Adicionar requisito</Button><Button variant="outlined" color="inherit" startIcon={<UndoIcon />} onClick={cancelarAlteracoes} disabled={salvando || !alterado} sx={{ flexShrink: 0 }}>Cancelar</Button><Button variant="contained" startIcon={<SaveIcon />} onClick={salvar} disabled={salvando || !alterado} sx={{ flexShrink: 0 }}>{salvando ? "Salvando..." : "Salvar"}</Button></Stack></Paper>
    <Paper ref={tabelaRef} onScroll={(event) => { if (barraInferiorRef.current) barraInferiorRef.current.scrollLeft = event.currentTarget.scrollLeft; }} variant="outlined" sx={{ overflowX: "auto", width: "100%", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}><Table key={versaoTabela} size="small" sx={{ minWidth: 1850, tableLayout: "fixed" }}><TableHead><TableRow>{CAMPOS_REQUISITO_LEGAL.map(([campo, titulo]) => <TableCell key={campo} sx={{ ...cabecalho, width: larguras[campo] }}>{titulo}</TableCell>)}<TableCell sx={{ ...cabecalho, width: 55 }} /></TableRow></TableHead><TableBody>{cadastro.requisitos.map((item, indice) => <TableRow key={indice}>{CAMPOS_REQUISITO_LEGAL.map(([campo]) => <TableCell key={campo} contentEditable suppressContentEditableWarning sx={{ ...celula, ...estiloPorCampo[campo], width: larguras[campo] }} onInput={() => setAlterado(true)} onBlur={(event) => atualizarRequisito(indice, campo, event.currentTarget.innerText)}>{item[campo] || ""}</TableCell>)}<TableCell sx={{ ...celula, cursor: "default" }}><Tooltip title="Remover requisito"><IconButton size="small" color="error" onClick={(evento) => { evento.stopPropagation(); setIndiceExclusao(indice); }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip></TableCell></TableRow>)}</TableBody></Table></Paper>
    <Box ref={barraInferiorRef} onScroll={(event) => { if (tabelaRef.current) tabelaRef.current.scrollLeft = event.currentTarget.scrollLeft; }} sx={{ position: "fixed", bottom: 0, left: 0, width: "100vw", height: 20, overflowX: "auto", overflowY: "hidden", bgcolor: "background.paper", borderTop: "1px solid #94a3b8", zIndex: 1300 }}><Box sx={{ width: larguraRolagem, height: 1 }} /></Box>
    </>}
  </Box><Dialog open={indiceExclusao !== null} onClose={fecharConfirmacaoExclusao} onClick={(evento) => evento.stopPropagation()}><DialogTitle>Excluir requisito</DialogTitle><DialogContent><DialogContentText>Tem certeza que deseja excluir este requisito?</DialogContentText></DialogContent><DialogActions><Button onClick={fecharConfirmacaoExclusao}>Cancelar</Button><Button color="error" variant="contained" onClick={confirmarExclusao}>Sim</Button></DialogActions></Dialog><Snackbar open={notificacao.aberta} autoHideDuration={5000} onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}><Alert severity={notificacao.tipo} onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}>{notificacao.mensagem}</Alert></Snackbar></>;
}
